import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppCard from "@toothfairy/shared-ui/AppCard";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppUser from "@toothfairy/shared-ui/AppUser";
import dayjs from "dayjs";
import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import {
  isFirstTrialStarted,
  isUserEnabledToOperate,
  isUserWithNoPaymentMethod,
  wasWorkspaceJustCreated,
} from "../../../App";
import envConfig from "../../../envConfig";
import Files from "../../API/Files";
import AppJoyRide from "../../Components/Joyride";
import PageTitle from "../../Components/PageTitle";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../Components/ToothFairyComponents/ToothFairyModal";
import Constants from "../../Constants";
import useUserHelperPreferences from "../../Hooks/useUserHelperPreferences";
import useOnboardingTour, { TOUR_FLAGS } from "../../Hooks/useOnboardingTour";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory, useLocation } from "../../Router";
import { isLargeScreen } from "../MainViewContainer";
import WorkspaceChip from "./Components/WorkspaceChip";
import WorkspaceModalContent from "./Components/WorkspaceModalContent";
import ToothFairyLoadingPlaceholder from "../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { getBillingPeriodParams } from "../../Constants/billing";
import utils from "../Common/utils";
const extraDay = 24 * 60 * 60 * 1000;

// Item-scoped mirror of useWorkspaces.getUoiBudget: derives the UoI budget for
// any workspace card (the hook only works for the ACTIVE workspace).
const getCardUoiBudget = (ws) => {
  const customUoiBudget = ws?.customUoiBudget;
  switch (ws?.subscriptionType) {
    case "base":
      return 500;
    case "pro":
      return 1500 * Math.max(ws?.maxUsers || 1, 1);
    case "business":
      return 5000 * Math.max(ws?.maxUsers || 5, 1);
    case "enterprise":
      if (customUoiBudget && customUoiBudget > 0) return customUoiBudget;
      return 7500 * Math.max(ws?.maxUsers || 10, 1);
    default:
      return 500;
  }
};

const formatCompactUoi = (num) =>
  new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(num || 0);

// --- Favourite workspace storage helpers (per device, localStorage) -------
const FAVOURITE_WORKSPACE_KEY = "tf_favourite_workspace";
// Custom same-window event: the browser "storage" event only fires in OTHER
// windows, so without this the star wouldn't repaint until the next unrelated
// re-render.
const FAVOURITE_WORKSPACE_EVENT = "tf_favourite_workspace_changed";

const readFavouriteWorkspaceId = () => {
  try {
    return localStorage.getItem(FAVOURITE_WORKSPACE_KEY) || null;
  } catch (_) {
    return null;
  }
};

// Timestamp of the last manual star toggle. If the workspace screen remounts
// right after the user stars a workspace, the auto-enter effect below would
// otherwise immediately yank them into it — starring is not a request to open.
let lastFavouriteToggleAt = 0;

const writeFavouriteWorkspaceId = (id) => {
  lastFavouriteToggleAt = Date.now();
  try {
    if (id) localStorage.setItem(FAVOURITE_WORKSPACE_KEY, id);
    else localStorage.removeItem(FAVOURITE_WORKSPACE_KEY);
  } catch (_) {}
  try {
    window.dispatchEvent(new Event(FAVOURITE_WORKSPACE_EVENT));
  } catch (_) {}
};

// Shared hook so screens stay in sync with the saved favourite.
const useFavouriteWorkspaceId = () => {
  const [value, setValue] = useState(readFavouriteWorkspaceId);
  useEffect(() => {
    const sync = () => setValue(readFavouriteWorkspaceId());
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    window.addEventListener(FAVOURITE_WORKSPACE_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
      window.removeEventListener(FAVOURITE_WORKSPACE_EVENT, sync);
    };
  }, []);
  return value ?? readFavouriteWorkspaceId();
};

// Fully-filled star (Font Awesome Pro 6 solid "star", f005). The bundled
// AppAssets.icons.star is the light/outline glyph, which stays hollow even
// when coloured — the favourited card needs a solid fill to read as "on".
const faStarSolidIcon = {
  prefix: "fas",
  iconName: "star",
  icon: [
    576,
    512,
    [],
    "f005",
    "M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z",
  ],
};

const WorkspacesScreen = ({
  workspaces,
  handleWorkSpaceCreation,
  handleWorkspaceEdit,
  handleWorkspaceDeletion,
  handleWorkspaceSelection,
  handleFormSubmit,
  uoiCosts = {},
  initialValues,
  formValidationSchema,
  setIsModalOpen,
  setInitialValues,
  isModalOpen,
  modalContentKey,
  setModalContentKey,
  selectedWorkspace,
  setSelectedWorkspace,
  setShareInputValue,
  shareInputValue,
  handleMembersChange,
  loadMembers,
  shareMembers,
  cancelPlanChange,
  targetPlan,
  setTargetPlan,
  workspaceMutationInProgress,
  onPlanSelection,
  onValuesChange,
  onSubmit,
  forceEnabled,
  handlePaymentAttempt = () => {},
  paymentReattempted,
  isReattemptingPayment,
  onLogoChange,
  workspaceLogo,
  setWorkspaceLogo,
  fileUploadInProgress,
  setFileUploadInProgress,
  fileUploadProgress,
  setFileUploadProgress,
  setWorkspaceLogoFilename,
  eligibleParentWorkspaces,
  hasExistingWorkspaces,
  ...props
}) => {
  const formRef = useRef();
  const { currentTheme } = AppTheme.useTheme();
  const dimensions = useWindowDimensions();
  const [isMouseOverNewWorkspace, setIsMouseOverNewWorkspace] = useState(false);
  const isNewWorkspaceDisabled = false;
  const { user } = AppUser.useUser();
  const AppToasty = AppToast.useToast();
  const { t, i18n } = useTranslation();
  dayjs.locale(i18n.language);

  const steps = [
    {
      target: ".create_workspace",
      content: t("workspaces.joyride.createWorkspaceStep"),
      placement: "left",
      disableBeacon: true,
    },
  ];
  if (workspaces?.length > 0) {
    steps.push({
      target: ".select_workspace",
      disableBeacon: true,
      content: t("workspaces.joyride.selectWorkspaceStep"),
    });
  }

  const [showShareInput, setShareInput] = useState(false);
  const coreViewHeight = useMemo(
    () => dimensions.height - 200,
    [dimensions.height]
  );
  const _styles = useMemo(
    () =>
      styleGenerator(currentTheme, coreViewHeight, dimensions, isLargeScreen()),
    [currentTheme, coreViewHeight, dimensions]
  );
  const showBillingRequired = false;
  // (isUserWithNoPaymentMethod() || !isBillingAddressSet()) && !isSSO();
  const history = useHistory();
  const location = useLocation();
  const [isNewWorkspaceLoading, setIsNewWorkspaceLoading] = useState(false);

  // Wrapper for form submission to detect workspace creation
  const handleFormSubmitWrapper = async (values) => {
    setIsNewWorkspaceLoading(true);
    wasWorkspaceJustCreated(true);

    const valuesWithLogo = {
      ...values,
      logo: null,
    };

    const result = await handleFormSubmit(valuesWithLogo);
    setTimeout(() => {
      setIsNewWorkspaceLoading(false);
    }, 5000);

    return result;
  };

  const {
    returnCleanedWorkspaces,
    isWorkspaceDisabled,
    isWorkspaceInCreation,
  } = useWorkspaces();
  const { state } = AppStateManager.useAppStateManager();
  const [workspaceLogos, setWorkspaceLogos] = useState({});

  // --- Favourite workspace (star on card) -------------------------------
  // Stored locally (per device) in localStorage; when set, the app boots
  // straight into that workspace instead of the selection list.
  const favouriteWorkspaceId = useFavouriteWorkspaceId();
  const handleToggleFavourite = (id) => {
    const next = favouriteWorkspaceId === id ? null : id;
    writeFavouriteWorkspaceId(next);
  };

  // Auto-open the favourited workspace once per session, before paint
  // (useLayoutEffect) so the workspace list never visibly flashes first.
  // Skipped when a workspace is already active (e.g. returning to the list)
  // or when the favourite no longer exists / is disabled.
  const didAutoEnterFavourite = useRef(false);
  useLayoutEffect(() => {
    if (didAutoEnterFavourite.current) return;
    if (!favouriteWorkspaceId || !workspaces?.length) return;
    if (state?.activeWorkspaceID) {
      // Already inside a workspace — never ambush the user again.
      didAutoEnterFavourite.current = true;
      return;
    }
    if (Date.now() - lastFavouriteToggleAt < 2000) {
      // The user just starred a workspace manually — don't yank them into it.
      didAutoEnterFavourite.current = true;
      return;
    }
    const fav = workspaces.find((w) => w?.id === favouriteWorkspaceId);
    if (!fav || isWorkspaceInCreation(fav)) return; // keep waiting for list
    // Mark even when the favourite can't be opened, so it retries exactly
    // once per session rather than every render.
    didAutoEnterFavourite.current = true;
    if (isWorkspaceDisabled(fav)) return;
    handleWorkspaceSelection(fav.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces, favouriteWorkspaceId, state?.activeWorkspaceID]);

  const loadWorkspaceLogo = async (workspaceId, logoFilename) => {
    if (!logoFilename) return null;

    try {
      const _url = await Files.downloadUrlGeneration({
        filename: `imported-image/${workspaceId}/${logoFilename}`,
        context: "pdf",
        workspaceid: workspaceId,
      });
      return _url?.data?.url || _url?.url || null;
    } catch (error) {
      console.error("Error loading workspace logo:", error);
      return null;
    }
  };

  useEffect(() => {
    const loadAllWorkspaceLogos = async () => {
      const logos = {};
      for (const workspace of workspaces || []) {
        if (workspace?.logo) {
          const logoUrl = await loadWorkspaceLogo(workspace.id, workspace.logo);
          if (logoUrl) {
            logos[workspace.id] = logoUrl;
          }
        }
      }
      setWorkspaceLogos(logos);
    };

    if (workspaces?.length > 0) {
      loadAllWorkspaceLogos();
    }
  }, [workspaces]);

  const getWorkspaceLogo = (workspaceId) => {
    return workspaceLogos[workspaceId] || null;
  };

  const { updateUserHelperPreferences, isUserHelperToBeDisplayed } =
    useUserHelperPreferences();

  const {
    needsTour,
    completeTourIfEligible,
    isPlatformSupported,
    useTourReady,
  } = useOnboardingTour();
  const workspaceTourReady = useTourReady([".create_workspace"]);

  // Determine layout type
  const cleanedWorkspaces = returnCleanedWorkspaces(workspaces, true);
  // Dev/QA escape hatch: force the single-workspace view even with multiple
  // workspaces so the layout can be reviewed before it becomes the default.
  const forceSingleWorkspace =
    new URLSearchParams(location?.search || "").get(
      "previewSingleWorkspace"
    ) === "1";
  const isSingleWorkspace =
    forceSingleWorkspace || cleanedWorkspaces?.length === 1;

  // Shared card component: rendered identically by BOTH the multi-workspace
  // grid and the single-workspace view so the two can never drift apart.
  const WorkspaceCardItem = ({ item, logoUrl }) => {
    // Notification-only flag: the account has hit the monthly spend limit
    // (workspace.disabled), which BLOCKS consumption but must NOT stop the user
    // from opening the workspace. Distinct from an expired subscription
    // (isWorkspaceDisabled), which does block opening.
    const isSpendLimitPaused =
      item?.disabled === true && item?.paymentFailed !== true;
    const isDisabled = isWorkspaceDisabled(item);
    const isCreation = isWorkspaceInCreation(item);
    const isOwner = item?.userID === user.id;
    // A sub-workspace must NEVER render the retry-payment action:
    // billing is owned by its parent root. Treat any parent
    // reference (including an empty/none placeholder) as a child.
    const isChildWorkspace =
      item?.parentWorkspaceID != null && item?.parentWorkspaceID !== "";
    // Exact UoI comes from the fetched monthCosts map (uoiCosts), never from the
    // stale aiConsumption counter on the record. While that fetch is pending the
    // card shows a translucent skeleton bar and hides the numbers entirely.
    const uoiStatus = uoiCosts?.[item.id];
    const uoiLoading = !uoiStatus || uoiStatus?.status === "loading";
    const uoiConsumed =
      uoiStatus?.status === "loaded" ? uoiStatus?.consumed ?? 0 : 0;
    const uoiBudget = getCardUoiBudget(item);
    const uoiPct = uoiLoading
      ? 0
      : Math.min((uoiConsumed / (uoiBudget || 1)) * 100, 100);
    const uoiColor =
      uoiPct > 90
        ? currentTheme?.theme?.red
        : uoiPct > 70
        ? currentTheme?.theme?.orange
        : currentTheme?.theme?.primary;

  const isFavorite = favouriteWorkspaceId === item?.id;
  return (
    <TouchableOpacity
        style={[
          _styles.workspaceCard,
          isDisabled && _styles.workspaceCardDisabled,
          isCreation && _styles.workspaceCardCreating,
        ]}
        onPress={() => {
          if (isDisabled && !wasWorkspaceJustCreated()) {
            if (item?.parentWorkspaceID) {
              AppToasty.show(
                t("workspaces.contactRootAdminOfParentWorkspace"),
                {
                  type: "danger",
                }
              );
            } else if (item?.userID === user?.id) {
              history.push("/profile/billing");
            } else {
              AppToasty.show(t("workspaces.contactRootAdminToReactivate"), {
                type: "danger",
              });
            }
          } else {
            // Spend limit is a notification, not a block — open the workspace
            // regardless, but toast so a non-owner understands the root admin's
            // account has reached its limit (consumption is enforced backend-side).
            if (isSpendLimitPaused && item?.userID !== user?.id) {
              AppToasty.show(t("workspaces.rootAdminLimitReached"), {
                type: "normal",
              });
            }
            handleWorkspaceSelection(item?.id);
          }
        }}
        disabled={isCreation}
        activeOpacity={0.9}
      >
        <View style={_styles.cardGradient}>
          <div
            className="workspace_favourite_toggle"
            aria-label="workspace-favourite-toggle"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleFavourite(isFavorite ? null : item?.id);
            }}
            style={_styles.favouriteStarButton}
          >
            <AppIcon
              icon={isFavorite ? faStarSolidIcon : AppAssets.icons.star}
              size={18}
              color={
                isFavorite
                  ? currentTheme?.theme?.orange
                  : currentTheme?.theme?.font_color
              }
            />
          </div>
          <View style={_styles.cardHeader}>
            {logoUrl ? (
              <View style={_styles.workspaceLogoContainer}>
                <img
                  src={logoUrl}
                  alt={item?.title}
                  style={{
                    maxWidth: 120,
                    maxHeight: 40,
                    objectFit: "contain",
                    borderRadius: 6,
                  }}
                />
              </View>
            ) : null}
            <View
              style={[_styles.cardHeaderText, !logoUrl && { marginLeft: 0 }]}
            >
              <AppText style={_styles.cardTitle} numberOfLines={1}>
                {isCreation ? t("common.settingUp") : item?.title}
              </AppText>
              {(isOwner || item?.parentWorkspaceID) && (
                <View style={_styles.ownerBadge}>
                  <AppIcon
                    icon={AppAssets.icons.admin}
                    size={12}
                    color={currentTheme?.theme?.white}
                  />
                  <AppText style={_styles.ownerBadgeText}>
                    {item?.parentWorkspaceID
                      ? t("workspaces.subWorkspace")
                      : t("workspaces.owner")}
                  </AppText>
                </View>
              )}
            </View>
          </View>

          <View style={_styles.cardStats}>
            <View style={_styles.statItem}>
              <AppIcon
                icon={AppAssets.icons.users}
                size={14}
                color={currentTheme?.theme?.primary}
              />
              <AppText style={_styles.statValue}>
                {item?.users?.length || 1}
              </AppText>
              <AppText style={_styles.statLabelCard}>
                {t("workspaces.members").toLowerCase()}
              </AppText>
            </View>
            <View style={_styles.statItem}>
              <AppIcon
                icon={AppAssets.icons.subscriptionType}
                size={14}
                color={
                  item?.subscriptionType === "pro" ||
                  item?.subscriptionType === "enterprise" ||
                  item?.subscriptionType === "business"
                    ? currentTheme?.theme?.orange
                    : currentTheme?.theme?.primary
                }
              />
              <AppText
                style={[
                  _styles.statValue,
                  (item?.subscriptionType === "pro" ||
                    item?.subscriptionType === "enterprise" ||
                    item?.subscriptionType === "business") && {
                    color: currentTheme?.theme?.orange,
                  },
                ]}
              >
                {utils.capitalizeFirstLetter(
                  utils.adaptSubscriptionName(item?.subscriptionType)
                )}
              </AppText>
            </View>
            <View style={_styles.statItem}>
              <AppIcon
                icon={AppAssets.icons.domain}
                size={14}
                color={currentTheme?.theme?.primary}
              />
              <AppText style={_styles.statValue}>
                {t(
                  `workspaces.form.domainOptions.${item?.domain?.toUpperCase()}`,
                  {
                    defaultValue:
                      utils.capitalizeFirstLetter(item?.domain) ||
                      t("workspaces.defaultValue"),
                  }
                )}
              </AppText>
            </View>
          </View>

          <View style={_styles.uoiRow}>
            {uoiLoading ? (
              <>
                <View style={_styles.uoiSkeletonTrack}>
                  <View style={_styles.uoiSkeletonFill} />
                </View>
                <View style={_styles.uoiSkeletonText} />
              </>
            ) : (
              <>
                <View style={_styles.uoiTrack}>
                  <View
                    style={[
                      _styles.uoiFill,
                      {
                        width: `${uoiPct}%`,
                        backgroundColor: uoiColor,
                      },
                    ]}
                  />
                </View>
                <AppText style={_styles.uoiText} numberOfLines={1}>
                  {`${formatCompactUoi(uoiConsumed)} / ${formatCompactUoi(
                    uoiBudget
                  )} ${t("dashboard.costingView.uoi", {
                    defaultValue: "UoI",
                  })}`}
                </AppText>
              </>
            )}
          </View>

          {user?.stripeCustomerData?.subscriptionIdOnTrial != null &&
            user?.stripeCustomerData?.subscriptionIdOnTrial ===
              item?.subscriptionId && (
              <View style={_styles.trialBanner}>
                <AppIcon
                  icon={AppAssets.icons.trial}
                  size={14}
                  color={currentTheme?.theme?.white}
                />
                <AppText style={_styles.trialText}>
                  {t("workspaces.trialActive")}
                </AppText>
              </View>
            )}

          {uoiPct >= 100 ? (
            <View style={_styles.uoiAlertCritical}>
              <View style={_styles.paymentAlertContent}>
                <AppIcon
                  icon={AppAssets.icons.warning}
                  size={14}
                  color={currentTheme?.theme?.white}
                />
                <AppText style={_styles.paymentAlertText}>
                  {t("workspaces.uoiDepleted", {
                    defaultValue: "No UoI available, increase now",
                  })}
                </AppText>
              </View>
            </View>
          ) : uoiPct >= 90 ? (
            <View style={_styles.uoiAlertWarning}>
              <View style={_styles.paymentAlertContent}>
                <AppIcon
                  icon={AppAssets.icons.warning}
                  size={14}
                  color={currentTheme?.theme?.white}
                />
                <AppText style={_styles.paymentAlertText}>
                  {t("workspaces.uoiAlmostDepleted", {
                    defaultValue: "UoI almost used up — increase your budget",
                  })}
                </AppText>
              </View>
            </View>
          ) : null}

          {isSpendLimitPaused && (
            <View style={_styles.trialBanner}>
              <AppIcon
                icon={AppAssets.icons.warning}
                size={14}
                color={currentTheme?.theme?.white}
              />
              <AppText style={_styles.trialText}>
                {t("workspaces.spendLimitReached", {
                  defaultValue: "Root monthly spend limit reached",
                })}
              </AppText>
            </View>
          )}

          {!item?.activeUntil && !item?.workspaceToken && (
            <View style={_styles.settingUpBanner}>
              <View style={_styles.settingUpContent}>
                <View style={_styles.settingUpSpinner} />
                <AppText style={_styles.settingUpText}>
                  {t("workspaces.configuring")}
                </AppText>
              </View>
            </View>
          )}

          <View style={_styles.cardFooter}>
            <AppText style={_styles.footerHint}>
              {isCreation
                ? t("common.pleaseWait")
                : item?.parentWorkspaceID
                ? t("workspaces.belongsTo", {
                    name:
                      workspaces?.find((ws) => ws.id === item.parentWorkspaceID)
                        ?.title || "",
                  })
                : item?.subscriptionType === "enterprise"
                ? t("workspaces.customAgreement")
                : t("workspaces.renewsOn", {
                    date: utils.capitalizeFirstLetter(
                      dayjs(
                        getBillingPeriodParams(item, dayjs())
                          ?.billingPeriodEnd || dayjs().endOf("month")
                      ).format("MMMM D, YYYY")
                    ),
                  })}
            </AppText>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  return state?.workspacesLoading ? (
    <ToothFairyLoadingPlaceholder caption="common.loading" brandedAnimation />
  ) : (
    <AppBaseScreen isScrollable={false}>
      <style>{`@keyframes uoiShimmer { 0% { background-position: 150% 0; } 100% { background-position: -150% 0; } }`}</style>
      <AppJoyRide
        handleCallback={(e) => {
          if (e?.status == "finished" || e?.status == "skipped") {
            completeTourIfEligible(TOUR_FLAGS.workspaceTourCompleted);
          }
        }}
        run={
          isPlatformSupported &&
          workspaceTourReady &&
          needsTour(TOUR_FLAGS.workspaceTourCompleted) &&
          (workspaces?.length || 0) <= 3
        }
        steps={steps}
      />

      <View
        style={{ paddingHorizontal: 10, paddingVertical: 10, height: "100%" }}
      >
        <View>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              marginTop: -5,
            }}
          >
            <PageTitle
              style={{ marginTop: 10 }}
              accessibilityLabel="workspaces-title"
            >
              {t("workspaces.title")}
            </PageTitle>
            {workspaces?.length > 0 && (
              <div
                className="create_workspace"
                onClick={handleWorkSpaceCreation}
                style={{
                  cursor: "pointer",
                }}
              >
                <ToothFairyButton
                  paddingLeft={10}
                  paddingRight={10}
                  style={{ marginTop: 10 }}
                  isDisabled={isNewWorkspaceDisabled}
                  title={t("navigation.newWorkspace")}
                  onPress={handleWorkSpaceCreation}
                />
              </div>
            )}
          </View>
          {showBillingRequired && (
            <AppText
              numberOfLines={4}
              color={currentTheme?.theme?.orange}
              fontWeight="bold"
              clickableViewWrapperStyle={{
                marginTop: 20,
              }}
              isClickable
              onPress={() => {
                if (!wasWorkspaceJustCreated())
                  history.push("/profile/billing");
              }}
            >
              {isFirstTrialStarted()
                ? "BillingSetupRequiredWhileOnTrial"
                : isUserWithNoPaymentMethod()
                ? "GenericBillingSetupRequired"
                : "GenericBillingAddressSetupRequired"}
            </AppText>
          )}
        </View>
        <View style={_styles.list}>
          <View
            style={{
              flexWrap: "wrap",
              justifyContent: "flex-start",
              flexDirection: "row",
            }}
            items={workspaces}
            contentContainerStyle={{
              padding: 10,
            }}
          >
            {workspaces?.length > 0 ? (
              isSingleWorkspace ? (
                <View style={_styles.multiWorkspaceContainer}>
                  <WorkspaceCardItem
                    item={cleanedWorkspaces[0]}
                    logoUrl={getWorkspaceLogo(cleanedWorkspaces[0]?.id)}
                  />
                </View>
              ) : (
                <View style={_styles.multiWorkspaceContainer}>
                  {returnCleanedWorkspaces(workspaces, true)?.map((item) => (
                    <WorkspaceCardItem
                      key={item.id}
                      item={item}
                      logoUrl={getWorkspaceLogo(item?.id)}
                    />
                  ))}
                </View>
              )
            ) : (
              <div
                className="select_workspace"
                style={{
                  margin: "auto",
                  cursor: "pointer",
                }}
                onClick={() => {
                  handleWorkSpaceCreation();
                }}
              >
                <AppPlaceholderView
                  onMouseEnter={() => setIsMouseOverNewWorkspace(true)}
                  onMouseLeave={() => setIsMouseOverNewWorkspace(false)}
                  accessibilityLabel="workspaces-placeholder"
                  fontColor={
                    !isNewWorkspaceDisabled && currentTheme?.theme?.primary
                  }
                  wrapperStyle={{
                    marginHorizontal: "auto",
                    marginTop: 120,
                    borderColor:
                      isMouseOverNewWorkspace && !isNewWorkspaceDisabled
                        ? currentTheme?.theme?.primary
                        : currentTheme?.theme?.border_color,
                    borderWidth: 1,
                    borderRadius: 10,
                    padding: 50,
                    maxWidth: 400,
                    maxHeight: 260,
                    cursor: !isNewWorkspaceDisabled ? "pointer" : "default",
                  }}
                  size={30}
                  // fontColor={currentTheme?.theme?.white}
                  // color={currentTheme?.theme?.white}
                  icon={AppAssets.icons.fairy}
                  mainCaption={
                    showBillingRequired
                      ? "workspaces.billingEmptyPlaceholder"
                      : "workspaces.emptyPlaceholder"
                  }
                  subCaption={
                    showBillingRequired
                      ? "workspaces.billingRequiredPlaceholderSub"
                      : "workspaces.emptyPlaceholderSub"
                  }
                />
              </div>
            )}
          </View>
        </View>
        {isModalOpen && (
          <ToothFairyModal
            headerContainerStyle={{
              paddingTop: 20,
              paddingHorizontal: 20,
            }}
            scrollViewStyle={{
              margin: 20,
              width: "95%",
            }}
            modalBackdrop={true}
            modalBackdropStatic={true}
            modalStyle={_styles.modalInput}
            modalTitle={
              modalContentKey === "NEW"
                ? t("workspaces.createNew")
                : modalContentKey === "UPDATE" || modalContentKey === "DELETE"
                ? `${t("workspaces.edit")}: ${selectedWorkspace?.title}`
                : modalContentKey === "BILLING"
                ? t("profile.billing")
                : ""
            }
            isCloseButtonVisible
            closeModal={() => {
              formRef?.current?.resetForm();
              setIsModalOpen(false);
              setInitialValues(Constants.initialWorkspaceValues);
              setWorkspaceLogo(null);
              setFileUploadInProgress(false);
              setFileUploadProgress(0);
              setWorkspaceLogoFilename(null);
            }}
            isModalVisible={isModalOpen}
            transparent={true}
          >
            <WorkspaceModalContent
              ref={formRef}
              showShareInput={showShareInput}
              setShareInput={setShareInput}
              modalContentKey={modalContentKey}
              handleFormSubmit={handleFormSubmitWrapper}
              // workspaceLogoFilename={workspaceLogoFilename}
              formValidationSchema={formValidationSchema}
              initialValues={initialValues}
              shareInputValue={shareInputValue}
              setShareInputValue={setShareInputValue}
              handleMembersChange={handleMembersChange}
              setModalContentKey={setModalContentKey}
              setIsModalOpen={setIsModalOpen}
              handleWorkspaceDeletion={handleWorkspaceDeletion}
              loadMembers={loadMembers}
              shareMembers={shareMembers}
              cancelPlanChange={cancelPlanChange}
              targetPlan={targetPlan}
              setTargetPlan={setTargetPlan}
              selectedWorkspace={selectedWorkspace}
              workspaceMutationInProgress={workspaceMutationInProgress}
              onPlanSelection={onPlanSelection}
              onValuesChange={onValuesChange}
              onSubmit={onSubmit}
              forceEnabled
              onLogoChange={onLogoChange}
              workspaceLogo={workspaceLogo}
              setWorkspaceLogo={setWorkspaceLogo}
              fileUploadInProgress={fileUploadInProgress}
              setFileUploadInProgress={setFileUploadInProgress}
              fileUploadProgress={fileUploadProgress}
              setFileUploadProgress={setFileUploadProgress}
              setWorkspaceLogoFilename={setWorkspaceLogoFilename}
              eligibleParentWorkspaces={eligibleParentWorkspaces}
              hasExistingWorkspaces={hasExistingWorkspaces}
              childWorkspacesCount={
                (workspaces || []).filter(
                  (ws) => ws.parentWorkspaceID === selectedWorkspace?.id
                ).length
              }
            />
          </ToothFairyModal>
        )}
      </View>
    </AppBaseScreen>
  );
};
const styleGenerator = (
  currentTheme,
  coreViewHeight,
  dimensions,
  isLargeScreen
) => {
  // Stripe-style translucent shimmer. Dark/blue themes define skeleton_color;
  // light does not — use that as the mode detector.
  const isDarkMode = currentTheme?.theme?.skeleton_color != null;
  const skeletonBase = isDarkMode
    ? "rgba(255,255,255,0.08)"
    : "rgba(0,0,0,0.08)";
  const skeletonShimmer = isDarkMode
    ? "rgba(255,255,255,0.20)"
    : "rgba(255,255,255,0.60)";
  return StyleSheet.create({
    title: { paddingRight: 10, flex: 1 },
    submitBotton: {
      marginTop: 20,
      alignSelf: "center",
      minWidth: 200,
    },
    list: {
      zIndex: -1,
      flex: 1,
      marginRight: -5,
      marginLeft: -10,
      overflowY: "auto",
      overflowX: "hidden",
    },
    workspaceItem: {
      alignSelf: "flex-start",
      flexDirection: "row",
      flex: 1,
      paddingTop: 10,
    },
    modalInput: {
      maxHeight: Math.min(dimensions.height - 40, 680),
      height: "auto",
      marginHorizontal: Platform.OS === "web" && isLargeScreen ? "20%" : "auto",
      marginVertical: "auto",
      width: Platform.OS === "web" && isLargeScreen ? "60%" : "95%",
    },
    multiWorkspaceContainer: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 20,
      padding: 20,
      justifyContent: "center",
      width: "100%",
    },
    workspaceCard: {
      width:
        dimensions?.width < 768
          ? "100%"
          : dimensions?.width < 1200
          ? "calc(50% - 10px)"
          : "calc(33.333% - 14px)",
      minWidth: 280,
      maxWidth: 380,
      height: 248,
      borderRadius: 16,
      backgroundColor: currentTheme?.theme?.backgroundColor,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
      overflow: "hidden",
      cursor: "pointer",
      transition: "all 0.3s ease",
      shadowColor: currentTheme?.theme?.shadow,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 4,
    },
    workspaceCardDisabled: {
      opacity: 0.7,
    },
    workspaceCardCreating: {
      opacity: 0.8,
    },
    favouriteStarButton: {
      position: "absolute",
      top: 10,
      right: 10,
      zIndex: 10,
      width: 32,
      height: 32,
      borderRadius: 16,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: currentTheme?.theme?.backgroundColor,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    cardGradient: {
      position: "relative",
      flex: 1,
      paddingTop: 24,
      paddingHorizontal: 24,
      paddingBottom: 12,
    },
    cardHeader: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 20,
    },
    workspaceLogoContainer: {
      height: 52,
      maxWidth: 140,
      justifyContent: "center",
      alignItems: "flex-start",
    },
    cardHeaderText: {
      flex: 1,
      marginLeft: 14,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: "bold",
      color: currentTheme?.theme?.font_color,
      maxWidth: 200,
    },
    ownerBadge: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: currentTheme?.theme?.primary,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 12,
      marginTop: 6,
      alignSelf: "flex-start",
    },
    ownerBadgeText: {
      fontSize: 11,
      fontWeight: "bold",
      color: currentTheme?.theme?.white,
      marginLeft: 4,
    },
    cardStats: {
      flexDirection: "row",
      alignItems: "center",
      gap: 20,
      marginBottom: 12,
      paddingHorizontal: 2,
    },
    statItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      minWidth: 0,
    },
    statValue: {
      fontSize: 13,
      fontWeight: "bold",
      color: currentTheme?.theme?.font_color,
    },
    statLabelCard: {
      fontSize: 12,
      color:
        currentTheme?.theme?.secondary_font_color ||
        currentTheme?.theme?.font_color,
    },
    trialBanner: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: currentTheme?.theme?.orange,
      paddingHorizontal: 14,
      paddingVertical: 8,
      borderRadius: 10,
      marginBottom: 12,
      alignSelf: "flex-start",
    },
    trialText: {
      fontSize: 12,
      fontWeight: "bold",
      color: currentTheme?.theme?.white,
      marginLeft: 6,
    },
    paymentAlert: {
      backgroundColor: currentTheme?.theme?.red,
      borderRadius: 10,
      paddingVertical: 8,
      paddingHorizontal: 12,
      marginBottom: 6,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    uoiAlertWarning: {
      backgroundColor: currentTheme?.theme?.orange,
      borderRadius: 10,
      paddingVertical: 6,
      paddingHorizontal: 12,
      marginBottom: 6,
      flexDirection: "row",
      alignItems: "center",
    },
    uoiAlertCritical: {
      backgroundColor: currentTheme?.theme?.red,
      borderRadius: 10,
      paddingVertical: 6,
      paddingHorizontal: 12,
      marginBottom: 6,
      flexDirection: "row",
      alignItems: "center",
    },
    paymentAlertContent: {
      flexDirection: "row",
      alignItems: "center",
    },
    paymentAlertText: {
      fontSize: 13,
      fontWeight: "bold",
      color: currentTheme?.theme?.white,
      marginLeft: 8,
    },
    retryButton: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: "rgba(255,255,255,0.2)",
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 6,
    },
    retryButtonText: {
      fontSize: 12,
      fontWeight: "bold",
      color: currentTheme?.theme?.white,
      marginLeft: 4,
    },
    settingUpBanner: {
      backgroundColor: currentTheme?.theme?.purple || "#8B5CF6",
      borderRadius: 10,
      padding: 12,
      marginBottom: 12,
    },
    settingUpContent: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    settingUpSpinner: {
      width: 14,
      height: 14,
      borderRadius: 7,
      borderWidth: 2,
      borderColor: currentTheme?.theme?.white,
      borderTopColor: "transparent",
      marginRight: 8,
    },
    settingUpText: {
      fontSize: 13,
      fontWeight: "bold",
      color: currentTheme?.theme?.white,
    },
    cardFooter: {
      paddingTop: 0,
      marginTop: "auto",
    },
    uoiRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      marginBottom: 10,
      paddingHorizontal: 2,
    },
    uoiTrack: {
      flex: 1,
      height: 4,
      borderRadius: 2,
      backgroundColor: currentTheme?.theme?.lightGrey,
      overflow: "hidden",
    },
    uoiSkeletonTrack: {
      flex: 1,
      height: 8,
      borderRadius: 4,
      overflow: "hidden",
      backgroundColor: skeletonBase,
    },
    uoiSkeletonFill: {
      height: "100%",
      borderRadius: 4,
      backgroundImage: `linear-gradient(90deg, ${skeletonBase} 25%, ${skeletonShimmer} 37%, ${skeletonBase} 63%)`,
      backgroundSize: "400% 100%",
      animation: "uoiShimmer 1.4s ease infinite",
    },
    uoiSkeletonText: {
      width: 72,
      height: 8,
      borderRadius: 4,
      backgroundColor: skeletonBase,
      flexShrink: 0,
    },
    uoiFill: {
      height: 4,
      borderRadius: 2,
    },
    uoiText: {
      fontSize: 11,
      color:
        currentTheme?.theme?.secondary_font_color ||
        currentTheme?.theme?.font_color,
      flexShrink: 0,
    },
    footerHint: {
      fontSize: 12,
      color: currentTheme?.theme?.mediumGrey || currentTheme?.theme?.font_color,
      textAlign: "left",
      fontStyle: "italic",
    },
  });
};

export default WorkspacesScreen;
